import React, { useState, useEffect } from 'react'
import { api, formatCurrency } from '../../utils/api'

// Design System (matching original warehouse-system)
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF',
    blueLight: '#EDF3F6'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Components
const Card = ({ children, style = {}, padding = true }) => (
  <div style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple },
    blue: { bg: theme.colors.blueLight, text: theme.colors.imBlue }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick }) => {
  const variants = {
    primary: { background: theme.colors.greenOnions, color: theme.colors.white },
    secondary: { background: theme.colors.charcoal, color: theme.colors.white },
    warning: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    danger: { background: theme.colors.crimson, color: theme.colors.white },
    ghost: { background: 'transparent', color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20 }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${theme.colors.grey200}`,
    borderTopColor: theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

// Progress Bar Component
const ProgressBar = ({ current, total, label }) => {
  const percent = total > 0 ? Math.round((current / total) * 100) : 0
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '14px', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: '14px', color: theme.colors.grey500 }}>{current} of {total}</span>
      </div>
      <div style={{
        height: '8px',
        background: theme.colors.grey200,
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${percent}%`,
          background: theme.colors.greenOnions,
          borderRadius: '4px',
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  )
}

function IssueTrades() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [itemSelections, setItemSelections] = useState({})
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  // Draft creation progress state
  const [draftProgress, setDraftProgress] = useState({
    status: 'idle', // 'idle' | 'creating' | 'done'
    current: 0,
    total: 0,
    results: []  // Array of { item, success, error }
  })

  useEffect(() => {
    loadDeals()
  }, [])

  async function loadDeals() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/api/warehouse/issue-trades')
      setDeals(response.deals || response.items || [])
    } catch (err) {
      setError(err.message || 'Failed to load issue trades')
    } finally {
      setLoading(false)
    }
  }

  function selectDeal(deal) {
    setSelectedDeal(deal)
    // Initialize selections - all items selected by default
    const selections = {}
    ;(deal.items || []).forEach(item => {
      selections[item.id] = true
    })
    setItemSelections(selections)
  }

  function toggleItemSelection(itemId) {
    setItemSelections(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }))
  }

  function keepAll() {
    const selections = {}
    ;(selectedDeal?.items || []).forEach(item => {
      selections[item.id] = true
    })
    setItemSelections(selections)
  }

  async function handleAccept() {
    if (!selectedDeal) return
    const keptItems = (selectedDeal.items || []).filter(item => itemSelections[item.id])
    const removedItems = (selectedDeal.items || []).filter(item => !itemSelections[item.id])

    if (keptItems.length === 0) {
      if (!confirm('No items selected. This will reject the entire trade and move it to Closed Lost. Continue?')) return
      // If no items kept, use reject-all
      return handleReject()
    }

    // Build item-by-item listing details
    const itemDetails = keptItems.map(item => {
      const condition = item.isBroken ? 'Spares or Repair' : (item.overallCondition || 'Good')
      const shortName = item.name?.length > 40 ? item.name.substring(0, 40) + '...' : item.name
      return `• ${shortName} → ${condition}`
    }).join('\n')

    // Confirmation for processing
    const msg = `This will create ${keptItems.length} Shopify draft${keptItems.length !== 1 ? 's' : ''}:\n\n` +
      itemDetails +
      `\n\nDeal moves to Awaiting Payment.` +
      (removedItems.length > 0 ? `\n${removedItems.length} rejected item${removedItems.length !== 1 ? 's' : ''} → Closed Lost` : '') +
      `\n\nContinue?`

    if (!confirm(msg)) return

    setProcessing(true)
    setError(null)

    // Initialize progress state
    setDraftProgress({
      status: 'creating',
      current: 0,
      total: keptItems.length,
      results: keptItems.map(item => ({ item, status: 'pending' }))
    })

    try {
      // Create drafts one at a time for progress tracking
      const results = []
      for (let i = 0; i < keptItems.length; i++) {
        const item = keptItems[i]

        // Update progress to show current item
        setDraftProgress(prev => ({
          ...prev,
          current: i,
          results: prev.results.map((r, idx) =>
            idx === i ? { ...r, status: 'creating' } : r
          )
        }))

        try {
          // Create individual draft
          await api.post('/api/warehouse/issue-trades/create-draft', {
            dealId: selectedDeal.id,
            item: item
          })

          results.push({ item, success: true })

          // Update progress with success
          setDraftProgress(prev => ({
            ...prev,
            current: i + 1,
            results: prev.results.map((r, idx) =>
              idx === i ? { ...r, status: 'success' } : r
            )
          }))
        } catch (err) {
          results.push({ item, success: false, error: err.message })

          // Update progress with failure
          setDraftProgress(prev => ({
            ...prev,
            current: i + 1,
            results: prev.results.map((r, idx) =>
              idx === i ? { ...r, status: 'failed', error: err.message } : r
            )
          }))
        }
      }

      // Finalize the deal (move stages, handle rejected items)
      await api.post('/api/warehouse/issue-trades/finalize', {
        dealId: selectedDeal.id,
        acceptedCount: results.filter(r => r.success).length,
        rejectedItems: removedItems
      })

      // Update progress to done
      setDraftProgress(prev => ({ ...prev, status: 'done' }))

      // Show summary after brief delay
      setTimeout(() => {
        const successCount = results.filter(r => r.success).length
        const failCount = results.filter(r => !r.success).length

        if (failCount === 0) {
          // All succeeded - auto close after showing results
          setTimeout(() => {
            setSelectedDeal(null)
            setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
            loadDeals()
          }, 2000)
        }
      }, 500)

    } catch (err) {
      setError(err.message || 'Failed to process trade')
      setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
    } finally {
      setProcessing(false)
    }
  }

  async function handleCreateRepair() {
    if (!selectedDeal) return
    const keptItems = (selectedDeal.items || []).filter(item => itemSelections[item.id])
    const brokenItems = keptItems.filter(item => item.isBroken)

    if (brokenItems.length === 0) return

    const itemDetails = brokenItems.map(item => {
      const shortName = item.name?.length > 40 ? item.name.substring(0, 40) + '...' : item.name
      return `• ${shortName} → ${item.brokenDetails || 'Broken'}`
    }).join('\n')

    const removedItems = (selectedDeal.items || []).filter(item => !itemSelections[item.id])

    const msg = `Send ${brokenItems.length} item${brokenItems.length !== 1 ? 's' : ''} to repair:\n\n` +
      itemDetails +
      `\n\nItems will enter the repair queue as "Not Started".\nNo Shopify draft will be created.\nDeal moves to Awaiting Payment.` +
      (removedItems.length > 0 ? `\n${removedItems.length} rejected → Closed Lost` : '') +
      `\n\nContinue?`

    if (!confirm(msg)) return

    setProcessing(true)
    setError(null)

    // Initialize progress state
    setDraftProgress({
      status: 'creating',
      current: 0,
      total: brokenItems.length,
      results: brokenItems.map(item => ({ item, status: 'pending' }))
    })

    try {
      await api.post('/api/warehouse/issue-trades/create-repair', {
        dealId: selectedDeal.id,
        items: brokenItems
      })

      // Finalize the deal (move to Awaiting Payment, handle rejected items)
      await api.post('/api/warehouse/issue-trades/finalize', {
        dealId: selectedDeal.id,
        acceptedCount: brokenItems.length,
        rejectedItems: removedItems
      })

      // Mark all as success
      setDraftProgress({
        status: 'done',
        current: brokenItems.length,
        total: brokenItems.length,
        results: brokenItems.map(item => ({ item, status: 'success' })),
        isRepair: true
      })

      // Auto close after showing results
      setTimeout(() => {
        setSelectedDeal(null)
        setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
        loadDeals()
      }, 2500)

    } catch (err) {
      setError(err.message || 'Failed to create repair items')
      setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
    } finally {
      setProcessing(false)
    }
  }

  async function handleReject() {
    if (!selectedDeal) return
    if (!confirm('Are you sure you want to reject this entire trade?\n\nNo Shopify drafts will be created.\nThe deal will be moved to Closed Lost.')) return

    setProcessing(true)
    setError(null)
    try {
      await api.post('/api/warehouse/issue-trades/reject-all', {
        dealId: selectedDeal.id,
        reason: 'Customer rejected revised offer'
      })
      alert('Deal moved to Closed Lost')
      setSelectedDeal(null)
      await loadDeals()
    } catch (err) {
      setError(err.message || 'Failed to reject trade')
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spinner size={40} />
      </div>
    )
  }

  // Deal Detail View
  if (selectedDeal) {
    const keptItems = (selectedDeal.items || []).filter(item => itemSelections[item.id])
    const removedItems = (selectedDeal.items || []).filter(item => !itemSelections[item.id])
    const keptTotal = keptItems.reduce((sum, item) => sum + (item.isBroken ? (item.sparesAmount || 0) : (item.buyPrice || 0)), 0)

    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '24px' }}>
        {/* Back Button */}
        <Button variant="ghost" size="sm" onClick={() => setSelectedDeal(null)} style={{ marginBottom: '24px' }}>
          ← Back
        </Button>

        {/* Deal Header */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: theme.colors.grey400, marginBottom: '4px' }}>Deal ID</div>
            <div style={{ fontFamily: theme.fonts.sans, fontSize: '16px', fontWeight: 600 }}>{selectedDeal.id}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: theme.colors.grey400, marginBottom: '4px' }}>Customer</div>
            <div style={{ fontSize: '15px', fontWeight: 500 }}>
              {selectedDeal.customerName || selectedDeal.dealName || selectedDeal.customerEmail}
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <Button variant="ghost" size="sm" onClick={keepAll}>Keep All</Button>
          <div style={{ flex: 1 }} />
          <Button variant="danger" size="sm" onClick={handleReject} disabled={processing}>
            Reject Entire Deal
          </Button>
        </div>

        {/* Items List */}
        <Card style={{ marginBottom: '24px' }} padding={false}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${theme.colors.grey200}`, fontWeight: 600 }}>
            Items ({(selectedDeal.items || []).length})
          </div>

          <div style={{ padding: '12px' }}>
            {(selectedDeal.items || []).map((item, index) => {
              const isKept = itemSelections[item.id]
              const effectivePrice = item.isBroken ? item.sparesAmount : item.buyPrice

              return (
                <div
                  key={item.id || index}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px', marginBottom: '8px',
                    background: isKept ? theme.colors.greenLight : theme.colors.grey100,
                    borderRadius: theme.radius.md,
                    opacity: isKept ? 1 : 0.5
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px', fontWeight: 500,
                      textDecoration: isKept ? 'none' : 'line-through',
                      marginBottom: '4px'
                    }}>
                      {item.name || item.productName}
                    </div>
                    {item.isBroken && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Badge color="red">Broken</Badge>
                        {item.brokenDetails && (
                          <span style={{ fontSize: '12px', color: theme.colors.grey400 }}>{item.brokenDetails}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ textAlign: 'right', minWidth: '70px' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>
                        {formatCurrency(effectivePrice || 0)}
                      </div>
                      {item.isBroken && item.buyPrice !== effectivePrice && (
                        <div style={{ fontSize: '11px', color: theme.colors.grey400, textDecoration: 'line-through' }}>
                          {formatCurrency(item.buyPrice || 0)}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => toggleItemSelection(item.id)}
                      style={{
                        width: '32px', height: '32px', borderRadius: '50%', border: 'none',
                        background: isKept ? theme.colors.grey100 : theme.colors.greenLight,
                        color: isKept ? theme.colors.grey400 : theme.colors.greenOnions,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '16px', fontWeight: 600
                      }}
                      title={isKept ? 'Remove item' : 'Keep item'}
                    >
                      {isKept ? '×' : '✓'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </Card>

        {/* Removed Items Summary */}
        {removedItems.length > 0 && (
          <Card style={{ marginBottom: '24px', background: theme.colors.grey100 }}>
            <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
              <strong>{removedItems.length} item{removedItems.length > 1 ? 's' : ''}</strong> will be moved to a new Closed Lost deal
            </div>
          </Card>
        )}

        {/* Summary */}
        <Card style={{ marginBottom: '24px', background: theme.colors.charcoal, color: theme.colors.white }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.7 }}>Payment Total</div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatCurrency(keptTotal)}</div>
            </div>
            <div style={{ fontSize: '13px', opacity: 0.7 }}>
              {keptItems.length} item{keptItems.length !== 1 ? 's' : ''}
            </div>
          </div>
        </Card>

        {error && (
          <div style={{
            background: theme.colors.redLight, color: theme.colors.crimson,
            padding: '12px 16px', borderRadius: theme.radius.md,
            fontSize: '14px', marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        {/* Progress Bar during draft creation */}
        {draftProgress.status === 'creating' && (
          <Card style={{ marginBottom: '16px' }}>
            <ProgressBar
              current={draftProgress.current}
              total={draftProgress.total}
              label={draftProgress.isRepair ? "Sending to Repair" : "Creating Shopify Drafts"}
            />
            <div style={{ marginTop: '12px' }}>
              {draftProgress.results.map((result, idx) => {
                const condition = result.item.isBroken ? 'Spares or Repair' : (result.item.overallCondition || 'Good')
                return (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 12px', marginBottom: '8px',
                    background: result.status === 'success' ? theme.colors.greenLight :
                               result.status === 'failed' ? theme.colors.redLight :
                               result.status === 'creating' ? theme.colors.amberLight :
                               theme.colors.grey100,
                    borderRadius: theme.radius.md
                  }}>
                    <span style={{ fontSize: '16px', width: '24px', textAlign: 'center' }}>
                      {result.status === 'success' ? '✅' :
                       result.status === 'failed' ? '❌' :
                       result.status === 'creating' ? <Spinner size={16} /> :
                       '⏳'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {result.item.name}
                      </div>
                      <div style={{ fontSize: '12px', color: theme.colors.grey500 }}>
                        {condition}
                      </div>
                    </div>
                    <span style={{ fontSize: '12px', color: theme.colors.grey400, flexShrink: 0 }}>
                      {result.status === 'pending' ? 'Waiting...' :
                       result.status === 'creating' ? 'Creating...' :
                       result.status === 'success' ? 'Created' :
                       'Failed'}
                    </span>
                  </div>
                )
              })}
            </div>
          </Card>
        )}

        {/* Completion Summary */}
        {draftProgress.status === 'done' && (
          <Card style={{ marginBottom: '16px', background: draftProgress.isRepair ? theme.colors.purpleLight : theme.colors.greenLight }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>{draftProgress.isRepair ? '🔧' : '✅'}</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: draftProgress.isRepair ? theme.colors.purple : theme.colors.greenOnions, marginBottom: '8px' }}>
                {draftProgress.isRepair ? 'Sent to Repair' : 'Drafts Created Successfully'}
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.grey500 }}>
                {draftProgress.isRepair
                  ? `${draftProgress.results.filter(r => r.status === 'success').length} item${draftProgress.results.filter(r => r.status === 'success').length !== 1 ? 's' : ''} added to repair queue`
                  : (<>{draftProgress.results.filter(r => r.status === 'success').length} of {draftProgress.total} drafts created
                    {draftProgress.results.some(r => r.status === 'failed') && (
                      <span style={{ color: theme.colors.crimson }}>
                        {' '}• {draftProgress.results.filter(r => r.status === 'failed').length} failed
                      </span>
                    )}</>)
                }
              </div>
              <div style={{ marginTop: '16px' }}>
                <Button variant="ghost" size="sm" onClick={() => {
                  setSelectedDeal(null)
                  setDraftProgress({ status: 'idle', current: 0, total: 0, results: [] })
                  loadDeals()
                }}>
                  ← Back to Issue Trades
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Info message about what will happen */}
        {keptItems.length > 0 && draftProgress.status === 'idle' && (
          <div style={{
            background: theme.colors.blueLight, color: theme.colors.imBlue,
            padding: '12px 16px', borderRadius: theme.radius.md,
            fontSize: '13px', marginBottom: '16px'
          }}>
            <strong>Shopify drafts to be created:</strong>
            <div style={{ marginTop: '8px' }}>
              {keptItems.map((item, idx) => {
                const condition = item.isBroken ? 'Spares or Repair' : (item.overallCondition || 'Good')
                return (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: idx < keptItems.length - 1 ? '1px solid rgba(78,128,152,0.2)' : 'none' }}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px' }}>
                      {item.name}
                    </span>
                    <span style={{ fontWeight: 600, flexShrink: 0 }}>
                      {condition}
                    </span>
                  </div>
                )
              })}
            </div>
            <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid rgba(78,128,152,0.3)' }}>
              Deal moves to Awaiting Payment
              {removedItems.length > 0 && <span> • {removedItems.length} rejected → Closed Lost</span>}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {draftProgress.status === 'idle' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Button
              onClick={handleAccept}
              disabled={processing}
              fullWidth
              size="lg"
            >
              {keptItems.length === 0 ? (
                'Move All to Closed Lost'
              ) : (
                `Create ${keptItems.length} Shopify Draft${keptItems.length !== 1 ? 's' : ''}`
              )}
            </Button>

            {keptItems.some(item => item.isBroken) && keptItems.length > 0 && (
              <Button
                variant="warning"
                onClick={handleCreateRepair}
                disabled={processing}
                fullWidth
                size="lg"
              >
                {`Create Repair (${keptItems.filter(i => i.isBroken).length} broken item${keptItems.filter(i => i.isBroken).length !== 1 ? 's' : ''})`}
              </Button>
            )}
          </div>
        )}

        {keptItems.length === 0 && draftProgress.status === 'idle' && (
          <p style={{ textAlign: 'center', fontSize: '13px', color: theme.colors.grey400, marginTop: '12px' }}>
            All items removed - deal will be moved to Closed Lost
          </p>
        )}
      </div>
    )
  }

  // List View
  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Issue Trades</h1>
          <p style={{ color: theme.colors.grey500, fontSize: '14px' }}>
            Deals with broken or rejected items awaiting resolution
          </p>
        </div>
        <Button variant="ghost" onClick={loadDeals}>↻ Refresh</Button>
      </div>

      {error && (
        <Card style={{ background: theme.colors.redLight, marginBottom: '20px' }}>
          <p style={{ color: theme.colors.crimson, margin: 0 }}>{error}</p>
        </Card>
      )}

      {deals.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>No Issue Trades</h3>
          <p style={{ color: theme.colors.grey500 }}>All deals are resolved</p>
        </Card>
      ) : (
        <div>
          {deals.map(deal => {
            const brokenCount = (deal.items || []).filter(i => i.isBroken).length
            const dealId = deal.id || deal.deal_id
            const dealName = deal.dealName || deal.deal_name || `Deal ${dealId}`
            const customerEmail = deal.customerEmail || deal.customer_email || ''
            const customerLastName = deal.customerName?.split(' ').pop() || ''

            return (
              <Card key={dealId} style={{ marginBottom: '16px' }} padding={false}>
                {/* Deal Header - Clickable */}
                <div
                  onClick={() => selectDeal(deal)}
                  style={{
                    padding: '20px', cursor: 'pointer',
                    borderBottom: `1px solid ${theme.colors.grey200}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, marginRight: '16px' }}>
                    {/* Deal Name (full item list) */}
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      marginBottom: '8px',
                      lineHeight: 1.4,
                      color: theme.colors.charcoal
                    }}>
                      {dealName}
                    </div>
                    {/* Broken count and customer info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Badge color={brokenCount > 0 ? 'red' : 'green'}>{brokenCount} broken</Badge>
                    </div>
                    <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '8px' }}>
                      {customerEmail} {customerLastName}
                    </div>
                    <div style={{ fontSize: '13px', color: theme.colors.imBlue, fontWeight: 500 }}>
                      Click to process this deal →
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: theme.colors.charcoal }}>
                      {formatCurrency(deal.amount || 0)}
                    </div>
                    <div style={{ fontSize: '13px', color: theme.colors.grey400 }}>
                      {deal.updatedAt && new Date(deal.updatedAt).toLocaleDateString('en-GB')}
                    </div>
                  </div>
                </div>

                {/* Items Breakdown */}
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.grey500, marginBottom: '12px' }}>
                    Items ({(deal.items || []).length})
                  </div>
                  {(deal.items || []).map((item, idx) => (
                    <div
                      key={item.id || idx}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                        padding: '12px',
                        background: theme.colors.white,
                        borderRadius: theme.radius.md,
                        marginBottom: '8px',
                        border: `1px solid ${theme.colors.grey200}`
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '16px', flexShrink: 0 }}>
                          {item.isBroken ? '❌' : '✅'}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                            {item.name || item.productName}
                          </div>
                          {item.isBroken && item.brokenDetails && (
                            <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
                              {item.brokenDetails}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <Badge color={item.isBroken ? 'red' : 'green'}>
                          {item.isBroken ? 'Failed - Broken' : `Pass - ${item.overallCondition || 'Unknown'}`}
                        </Badge>
                        {item.isBroken && (
                          <div style={{ fontSize: '13px', color: theme.colors.grey500, marginTop: '4px' }}>
                            Spares: {formatCurrency(item.sparesAmount || 0)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default IssueTrades
